/**
 * ConfirmProvider — diálogo de confirmación, promesa-based, una sola
 * implementación para toda la app. Reemplaza `window.confirm()` 1:1:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: '¿Eliminar esta consulta?',
 *     message: 'Paciente: …\n\nEsta acción no se puede deshacer.',
 *     confirmLabel: 'Eliminar',
 *     tone: 'danger',
 *   });
 *   if (!ok) return;
 *
 * A diferencia del nativo: theme-aware, no bloquea el hilo, foco atrapado,
 * `Esc` y click-afuera cancelan, y devuelve el foco al elemento que lo abrió.
 * Montado una vez en App.tsx; renderiza vía createPortal en <body>.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './ui.css';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface DialogState {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DialogState | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      lastFocused.current = document.activeElement as HTMLElement | null;
      setState({ opts, resolve });
    });
  }, []);

  const close = useCallback(
    (result: boolean) => {
      setState((current) => {
        current?.resolve(result);
        return null;
      });
    },
    [],
  );

  // Scroll lock + foco inicial + restaurar foco al cerrar
  useEffect(() => {
    if (!state) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // foco: en danger va al botón menos destructivo (cancelar)
    const target = state.opts.tone === 'danger' ? cancelBtnRef.current : confirmBtnRef.current;
    target?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      lastFocused.current?.focus?.();
    };
  }, [state]);

  // Esc + trampa de foco
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = dialogRef.current?.querySelectorAll<HTMLElement>('button');
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [close],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state &&
        createPortal(
          <div
            className="ccui-dialog-scrim"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close(false);
            }}
          >
            <div
              ref={dialogRef}
              className="ccui-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="ccui-dialog-title"
              aria-describedby={state.opts.message ? 'ccui-dialog-message' : undefined}
              onKeyDown={onKeyDown}
            >
              <h2 id="ccui-dialog-title" className="ccui-dialog__title">
                {state.opts.title}
              </h2>
              {state.opts.message && (
                <p id="ccui-dialog-message" className="ccui-dialog__message">
                  {state.opts.message}
                </p>
              )}
              <div className="ccui-dialog__actions">
                <button
                  ref={cancelBtnRef}
                  type="button"
                  className="ccui-btn ccui-btn--ghost"
                  onClick={() => close(false)}
                >
                  {state.opts.cancelLabel ?? 'Cancelar'}
                </button>
                <button
                  ref={confirmBtnRef}
                  type="button"
                  className={`ccui-btn ${
                    state.opts.tone === 'danger' ? 'ccui-btn--danger' : 'ccui-btn--primary'
                  }`}
                  onClick={() => close(true)}
                >
                  {state.opts.confirmLabel ?? 'Confirmar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  }
  return ctx;
}
